const BOX_DIMENSIONS_PATTERN = /^\s*(\d+(?:\.\d+)?)\s*[xX×]\s*(\d+(?:\.\d+)?)\s*[xX×]\s*(\d+(?:\.\d+)?)(?![\d.])/;
const BOX_FIT_TOLERANCE = 0.10;

function normalizeDimensions(dimensions) {
  if (!Array.isArray(dimensions) || dimensions.length !== 3) return null;

  const values = dimensions.map(Number);
  if (values.some((value) => !Number.isFinite(value) || value <= 0)) return null;

  const [length, width, height] = values.sort((a, b) => b - a);

  return { length, width, height };
}

function parseDimensions(value) {
  if (typeof value !== 'string') return null;

  const match = value.replace(/,/g, '.').match(BOX_DIMENSIONS_PATTERN);
  if (match === null) return null;

  return normalizeDimensions(match.slice(1, 4));
}

function getDimensionsKey(dimensions) {
  const normalized = normalizeDimensions([
    dimensions?.length,
    dimensions?.width,
    dimensions?.height,
  ]);

  if (normalized === null) return null;

  return `${normalized.length}x${normalized.width}x${normalized.height}`;
}

function getVolume(dimensions) {
  return dimensions.length * dimensions.width * dimensions.height;
}

function normalizeBoxes(boxes) {
  if (!Array.isArray(boxes)) return [];

  return boxes
    .map((box) => {
      const normalized = normalizeDimensions([box.length, box.width, box.height]);
      return normalized === null ? null : { ...box, ...normalized };
    })
    .filter((box) => box !== null);
}

function findSmallestFittingNormalizedBox(dimensions, normalizedBoxes, tolerance) {
  const target = normalizeDimensions([
    dimensions?.length,
    dimensions?.width,
    dimensions?.height,
  ]);

  if (target === null) return null;

  const targetKey = getDimensionsKey(target);
  const exactMatch = normalizedBoxes
    .filter((box) => getDimensionsKey(box) === targetKey)
    .sort((a, b) => Number(a.id) - Number(b.id))[0];

  if (exactMatch !== undefined) return exactMatch;

  const minimum = {
    length: target.length * (1 - tolerance),
    width: target.width * (1 - tolerance),
    height: target.height * (1 - tolerance),
  };

  const candidates = normalizedBoxes
    .filter((box) => box.length >= minimum.length
      && box.width >= minimum.width
      && box.height >= minimum.height)
    .sort((a, b) => getVolume(a) - getVolume(b) || Number(a.id) - Number(b.id));

  return candidates[0] || null;
}

function findSmallestFittingBox(dimensions, boxes, tolerance = BOX_FIT_TOLERANCE) {
  return findSmallestFittingNormalizedBox(dimensions, normalizeBoxes(boxes), tolerance);
}

function applyAutomaticBoxSelection(data, existing, boxes) {
  const result = { ...data };
  const locked = Object.prototype.hasOwnProperty.call(data, 'box_id_locked')
    ? data.box_id_locked === true || Number(data.box_id_locked) === 1
    : existing?.box_id_locked === true || Number(existing?.box_id_locked) === 1;

  if (locked) return result;

  const shipDimensions = Object.prototype.hasOwnProperty.call(data, 'ship_dims')
    ? data.ship_dims
    : existing?.ship_dims;

  if (shipDimensions === undefined) return result;

  const box = findSmallestFittingBox(parseDimensions(shipDimensions), boxes);

  result.box_id = box?.id || null;
  result.box_id_locked = false;

  return result;
}

export { applyAutomaticBoxSelection, findSmallestFittingBox, parseDimensions };
